import React from 'react';
import {
  Alert,
  Box,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import AlertLivenessSourcesSettings from '../AlertLivenessSourcesSettings';

/**
 * Settings → Notification preferences (in-app alert behavior).
 * @param {{ userData?: { companyId?: string, role?: string }, showAlertsOnOverview?: boolean, onChangeShowAlertsOnOverview?: (v: boolean) => void }} props
 */
export default function NotificationPreferencesPanel({
  userData,
  showAlertsOnOverview,
  onChangeShowAlertsOnOverview,
  embedded = false,
}) {
  const canConfigureLiveness = Boolean(
    userData?.companyId && ['company_admin', 'developer'].includes(userData?.role),
  );

  return (
    <Box>
      {!embedded && (
        <>
          <Typography variant="h4" gutterBottom>
            Notification preferences
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Control how alerts appear in your inbox, on the overview dashboard, and from automated
            monitoring sources.
          </Typography>
        </>
      )}

      {embedded ? (
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
          Preferences
        </Typography>
      ) : (
        <Alert severity="info" sx={{ mb: 3, maxWidth: 640 }}>
          Your inbox is always available from the bell menu in the top bar.
        </Alert>
      )}

      {userData?.companyId && (
        <Box sx={{ maxWidth: 640, mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Overview dashboard
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showAlertsOnOverview}
                onChange={(_, checked) => onChangeShowAlertsOnOverview?.(checked)}
              />
            }
            label="Show Notifications section on Overview"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, ml: 4.5 }}>
            Turn off to hide the collapsible Notifications block on the home dashboard. The bell
            menu is unchanged.
          </Typography>
        </Box>
      )}

      {canConfigureLiveness && (
        <Box sx={{ maxWidth: 720 }}>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Automated monitoring sources
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Company admins and developers can tune which uptime events post to the team inbox.
          </Typography>
          <AlertLivenessSourcesSettings userData={userData} />
        </Box>
      )}
    </Box>
  );
}
